/**
 * Martina London — Collection PLP
 * Toolbar grid-column toggle + multi-variant quick add.
 */
(function () {
  'use strict';

  /* ----------------------------------------------------------
   * Grid toggle
   * Desktop (≥768px): 4-up ↔ 2-up
   * Mobile (<768px):  2-up ↔ 1-up
   * Persists choice per breakpoint in localStorage.
   * -------------------------------------------------------- */
  const GRID_KEY        = 'martina-london-grid-mode';
  const GRID_KEY_MOBILE = 'martina-london-grid-mode-mobile';
  const gridToggle = document.querySelector('[data-grid-toggle]');
  const grid       = document.querySelector('[data-collection-grid]');

  function isMobile() {
    return window.matchMedia('(max-width: 767px)').matches;
  }

  if (gridToggle && grid) {
    // Restore saved state on load
    try {
      if (isMobile()) {
        if (localStorage.getItem(GRID_KEY_MOBILE) === '1') {
          grid.classList.add('is-grid-1');
          gridToggle.setAttribute('data-mobile-mode', '1');
        }
      } else {
        if (localStorage.getItem(GRID_KEY) === '2') {
          gridToggle.dataset.gridMode = '2';
          grid.classList.add('is-grid-2');
          const label = gridToggle.querySelector('.collection-toolbar__grid-label');
          if (label) label.textContent = 'Compact view';
        }
      }
    } catch (_) {}

    gridToggle.addEventListener('click', () => {
      if (isMobile()) {
        const isOneUp = grid.classList.contains('is-grid-1');
        grid.classList.toggle('is-grid-1', !isOneUp);
        if (isOneUp) {
          gridToggle.removeAttribute('data-mobile-mode');
        } else {
          gridToggle.setAttribute('data-mobile-mode', '1');
        }
        try {
          localStorage.setItem(GRID_KEY_MOBILE, isOneUp ? '2' : '1');
        } catch (_) {}
      } else {
        const newMode = gridToggle.dataset.gridMode === '4' ? '2' : '4';
        gridToggle.dataset.gridMode = newMode;
        grid.classList.toggle('is-grid-2', newMode === '2');
        const label = gridToggle.querySelector('.collection-toolbar__grid-label');
        if (label) label.textContent = newMode === '2' ? 'Compact view' : 'Grid view';
        try {
          localStorage.setItem(GRID_KEY, newMode);
        } catch (_) {}
      }
    });
  }

  /* ----------------------------------------------------------
   * Quick add
   * Single variant  → add directly on trigger click.
   * Multi-variant   → expand size picker, add on variant click.
   * -------------------------------------------------------- */
  async function addVariantToCart(variantId, sourceBtn) {
    sourceBtn.disabled = true;
    const originalLabel = sourceBtn.textContent.trim();
    sourceBtn.textContent = '…';

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 })
      });
      if (!res.ok) throw new Error('Add to cart failed');

      if (typeof window.refreshCartDrawer === 'function') await window.refreshCartDrawer();
      if (typeof window.updateBagCount === 'function') await window.updateBagCount();
      if (typeof window.openCartDrawer === 'function') window.openCartDrawer();

      sourceBtn.textContent = '✓';

      const container = sourceBtn.closest('[data-quick-add-container]');
      setTimeout(() => {
        if (container) {
          container.dataset.expanded = 'false';
          const panel = container.querySelector('[data-quick-add-variants]');
          if (panel) panel.hidden = true;
          const picker = container.querySelector('[data-pc-picker]');
          if (picker) picker.hidden = true;
          const trigger = container.querySelector('[data-quick-add-trigger]');
          if (trigger) {
            trigger.setAttribute('aria-expanded', 'false');
            trigger.textContent = trigger.dataset.originalLabel || 'Add to bag +';
          }
        }
        sourceBtn.textContent = originalLabel;
        sourceBtn.disabled = false;
      }, 1000);
    } catch (_) {
      sourceBtn.textContent = 'Try again';
      setTimeout(() => {
        sourceBtn.textContent = originalLabel;
        sourceBtn.disabled = false;
      }, 1500);
    }
  }

  /* ----------------------------------------------------------
   * Mobile two-step picker — colour → size
   * Built lazily on first tap; cached on the container.
   * -------------------------------------------------------- */
  function buildMobilePicker(container) {
    const variantBtns = container.querySelectorAll('[data-pc-o2]');
    if (!variantBtns.length) return null;

    // Group sizes by colour (option2 → [{o1, vid, avail}])
    const coloursMap = new Map();
    variantBtns.forEach((btn) => {
      const o2 = btn.dataset.pcO2;
      if (!coloursMap.has(o2)) coloursMap.set(o2, []);
      coloursMap.get(o2).push({
        o1: btn.dataset.pcO1,
        vid: btn.dataset.variantId,
        avail: !btn.disabled,
      });
    });

    if (coloursMap.size <= 1) return null; // single colour: fall back to flat list

    const picker = document.createElement('div');
    picker.className = 'collection-product__mobile-picker';
    picker.setAttribute('data-pc-picker', '');

    const colourStep = document.createElement('div');
    colourStep.className = 'pc-step pc-step--colour';
    colourStep.setAttribute('data-pc-colour-step', '');

    const sizeStep = document.createElement('div');
    sizeStep.className = 'pc-step pc-step--size';
    sizeStep.setAttribute('data-pc-size-step', '');

    coloursMap.forEach((sizes, colour) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'collection-product__variant pc-colour-btn';
      btn.textContent = colour;
      btn.dataset.pcColour = colour;
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        // Highlight selected colour
        colourStep.querySelectorAll('.pc-colour-btn').forEach((b) =>
          b.classList.toggle('is-selected', b === btn)
        );
        // Populate sizes
        sizeStep.innerHTML = '';
        sizes.forEach(({ o1, vid, avail }) => {
          const sBtn = document.createElement('button');
          sBtn.type = 'button';
          sBtn.className = 'collection-product__variant pc-size-btn';
          sBtn.textContent = o1;
          sBtn.dataset.variantId = vid;
          if (!avail) { sBtn.disabled = true; sBtn.setAttribute('aria-disabled', 'true'); }
          sBtn.addEventListener('click', async (ev) => {
            ev.preventDefault();
            ev.stopPropagation();
            await addVariantToCart(vid, sBtn);
          });
          sizeStep.appendChild(sBtn);
        });
        sizeStep.hidden = false;
      });
      colourStep.appendChild(btn);
    });

    picker.appendChild(colourStep);
    picker.appendChild(sizeStep);
    return picker;
  }

  function openMobilePicker(container, trigger) {
    let picker = container.querySelector('[data-pc-picker]');
    if (!picker) {
      picker = buildMobilePicker(container);
      if (!picker) {
        // Fallback: show flat list
        const panel = container.querySelector('[data-quick-add-variants]');
        if (panel) {
          container.dataset.expanded = 'true';
          panel.hidden = false;
          trigger.setAttribute('aria-expanded', 'true');
        }
        return;
      }
      picker.hidden = true;
      const variantsPanel = container.querySelector('[data-quick-add-variants]');
      container.insertBefore(picker, variantsPanel || null);
    }

    // Reset state
    picker.querySelectorAll('.pc-colour-btn').forEach((b) => b.classList.remove('is-selected'));
    const sizeStep = picker.querySelector('[data-pc-size-step]');
    if (sizeStep) { sizeStep.innerHTML = ''; sizeStep.hidden = true; }

    picker.hidden = false;
    container.dataset.expanded = 'true';
    trigger.setAttribute('aria-expanded', 'true');
  }

  /* ----------------------------------------------------------
   * Trigger buttons (expand panel OR add directly for single-variant)
   * -------------------------------------------------------- */
  document.querySelectorAll('[data-quick-add-trigger]').forEach((trigger) => {
    trigger.dataset.originalLabel = trigger.textContent.trim();

    trigger.addEventListener('click', async (e) => {
      e.preventDefault();
      e.stopPropagation();

      const container = trigger.closest('[data-quick-add-container]');
      const variantsPanel = container ? container.querySelector('[data-quick-add-variants]') : null;
      const directVariantId = trigger.dataset.variantId;

      if (directVariantId) {
        await addVariantToCart(directVariantId, trigger);
        return;
      }

      // Mobile multi-option: two-step colour → size picker
      if (isMobile() && container && 'multiOption' in container.dataset) {
        openMobilePicker(container, trigger);
        return;
      }

      if (variantsPanel && container) {
        container.dataset.expanded = 'true';
        variantsPanel.hidden = false;
        trigger.setAttribute('aria-expanded', 'true');
      }
    });
  });

  // Variant size buttons (desktop flat list)
  document.querySelectorAll('[data-quick-add]:not([data-quick-add-trigger])').forEach((btn) => {
    btn.addEventListener('click', async (e) => {
      e.preventDefault();
      e.stopPropagation();
      await addVariantToCart(btn.dataset.variantId, btn);
    });
  });

  // Collection card video hover — play on mouseenter, pause+reset on mouseleave
  document.querySelectorAll('[data-has-video]').forEach((card) => {
    const video = card.querySelector('[data-card-video]');
    if (!video) return;
    card.addEventListener('mouseenter', () => video.play().catch(() => {}));
    card.addEventListener('mouseleave', () => {
      video.pause();
      video.currentTime = 0;
    });
  });

  // Click outside → collapse all expanded panels
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-quick-add-container]')) return;
    document.querySelectorAll('[data-quick-add-container][data-expanded="true"]').forEach((container) => {
      container.dataset.expanded = 'false';
      const panel = container.querySelector('[data-quick-add-variants]');
      if (panel) panel.hidden = true;
      const picker = container.querySelector('[data-pc-picker]');
      if (picker) picker.hidden = true;
      const trigger = container.querySelector('[data-quick-add-trigger]');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
    });
  });
})();
